// One step as a card in read mode: name (ⓘ = the hover text) + Produces chips · Wait · Then · ruler · Branches · Records. Nothing editable.
import { Wand2 } from "lucide-react";
import { InfoHover, ROLE_LABEL } from "./designerShared";
import { StepRuler } from "./StepRuler";
import { DAY_BASIS_LABEL, actionsOf, dateRefOptions, dueShort, dueText } from "./timelineHelp";

const CHIP = {
  doc: "bg-slate-100 text-slate-700 border-slate-200",
  wizard: "bg-rose-50 text-rose-800 border-rose-200",
  branch: "bg-amber-50 text-amber-900 border-amber-200",
};
export function Chip({ kind, children, testid }) {
  return <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-px rounded-full border whitespace-nowrap ${CHIP[kind]}`} data-testid={testid}>{kind === "wizard" && <Wand2 className="w-2.5 h-2.5" />}{children}</span>;
}

function Line({ label, children, testid, className = "" }) {
  return (
    <div className={`grid grid-cols-[52px_1fr] gap-x-2 ${className}`} data-testid={testid}>
      <span className="text-[9px] uppercase tracking-wider text-slate-400 pt-0.5">{label}</span>
      <div className="min-w-0 text-slate-700 leading-snug">{children}</div>
    </div>
  );
}

export function StepCard({ s, items, vocab, gates }) {
  const t = s.timer || {};
  const hasPeriod = t.days != null && t.days !== "" && Number(t.days) > 0;
  const period = hasPeriod ? { days: Number(t.days), basis: t.day_basis || "calendar" } : null;
  const refs = dateRefOptions(items, s);
  const acts = actionsOf(s).map((a) => ({ ...a, due_ref_label: refs.find((o) => o.value === (a.due_ref || "hearing_date"))?.label }));
  const vl = (field, k) => (vocab?.[field] || []).find((o) => o.key === k)?.label || k;
  const nameOf = (id) => items.find((x) => x.id === id)?.name || id;
  const docs = s.documents || [], wiz = (s.wizards || []).map((k) => vl("wizards", k)), subs = (s.sub_timelines || []).map(nameOf), caps = s.capture || [];
  const g = s.gate ? gates?.find((x) => x.key === s.gate) : null;
  const gateItems = (s.gate_items || []).length ? s.gate_items.map((x) => x.label) : g ? g.items.map((x) => x.label) : [];
  const deadline = hasPeriod && t.deadline_on_calendar !== false && !acts.some((a) => a.due_mode === "period_end");
  return (
    <div className={`border rounded-md bg-white px-3 py-2 ${s.retired ? "opacity-50" : ""}`} data-testid={`tl-read-row-${s.id}`}>
      <div className="flex items-start gap-x-3 gap-y-1 flex-wrap">
        <div className="min-w-0 flex-1 flex items-center gap-1.5 flex-wrap text-[13px] font-semibold text-slate-900">
          {s.hover ? <InfoHover help={{ title: s.name, lines: [s.hover] }} testid={`tl-read-hover-${s.id}`}><span>{s.name}</span></InfoHover> : <span>{s.name}</span>}
          <span className="font-mono text-[9px] font-normal text-slate-400">{s.id}</span>
          {s.terminal && <span className="text-[9px] font-normal text-slate-400">terminal</span>}
          {s.retired && <span className="text-[9px] font-normal text-slate-400">retired</span>}
          {s.applies_to && s.applies_to !== "both" && <span className="text-[9px] font-normal text-slate-400">{s.applies_to} only</span>}
          {(s.gate || gateItems.length > 0) && <span className="text-[9px] font-medium px-1.5 py-px rounded border bg-indigo-50 text-indigo-800 border-indigo-200" title={gateItems.join("\n")} data-testid={`tl-read-gate-${s.id}`}>Gate {s.gate || ""}{g?.label ? ` · ${g.label}` : ""}{gateItems.length ? ` · ${gateItems.length} checks` : ""}</span>}
        </div>
        {(docs.length > 0 || wiz.length > 0) && (
          <div className="flex items-center gap-1 flex-wrap" data-testid={`tl-read-produces-${s.id}`}>
            {docs.map((d, i) => <Chip key={`d${i}`} kind="doc">{d}</Chip>)}
            <span className="contents" data-testid={`tl-read-wizards-${s.id}`}>{wiz.map((w, i) => <Chip key={`w${i}`} kind="wizard">{w}</Chip>)}</span>
          </div>
        )}
      </div>
      <div className="mt-1.5 space-y-0.5 text-[11px]" data-testid={`tl-read-after-${s.id}`}>
        {hasPeriod && (
          <Line label="Wait" testid={`tl-read-wait-${s.id}`}>
            <strong className="text-slate-900">{period.days} {DAY_BASIS_LABEL[period.basis]}</strong>{period.basis === "clear" ? " (CPR 2.8)" : ""}{t.meaning ? ` — ${t.meaning}` : ""}
            {deadline ? <span className="text-slate-500"> · the end goes on the calendar as a deadline</span> : null}{t.prompt === false ? <span className="text-slate-500"> · no prompt</span> : null}{t.attention_after_wd != null && t.attention_after_wd !== "" ? <span className="text-slate-500"> · attention after {t.attention_after_wd} wd</span> : null}
          </Line>
        )}
        {acts.map((a, i) => (
          <Line key={a.id || i} label={i === 0 ? "Then" : ""} testid={`tl-read-action-${s.id}-${i}`}>
            <span className={`inline-block min-w-[58px] mr-1.5 font-mono text-[10px] ${a.due_mode === "before_date" ? "text-amber-800" : "text-rose-800"}`} title={`due ${dueText(a, period)}`}>{dueShort(a, period)}</span>
            <span className="font-medium text-slate-900">{a.label}</span>
            <span className="text-slate-500"> · {ROLE_LABEL[a.owner_role] || a.owner_role}{a.mandatory ? " · mandatory" : ""}{a.sla_days != null && a.sla_days !== "" ? ` · SLA ${a.sla_days} wd` : ""}</span>
            {a.due_mode === "before_date" && <span className="text-amber-800"> · noted until the {a.due_ref_label || "Hearing date"} is on the file</span>}
          </Line>
        ))}
        {!hasPeriod && !acts.length && <div className="text-slate-400 italic" data-testid={`tl-read-none-${s.id}`}>Nothing goes on the calendar after this step.</div>}
      </div>
      <StepRuler period={period} actions={acts} deadline={deadline} testid={`tl-read-ruler-${s.id}`} />
      {(subs.length > 0 || caps.length > 0) && (
        <div className="mt-1.5 pt-1.5 border-t border-dashed border-slate-200 space-y-0.5 text-[11px]">
          {subs.length > 0 && <Line label="Branches" testid={`tl-read-branches-${s.id}`}><span className="flex flex-wrap gap-1">{subs.map((n, i) => <Chip key={i} kind="branch">{n}</Chip>)}</span></Line>}
          {caps.length > 0 && <Line label="Records" testid={`tl-read-records-${s.id}`}>{caps.map((c) => `${c.role === "step_date" ? "★ " : ""}${c.label}${c.type && c.type !== "text" ? ` (${c.type})` : ""}`).join(" · ")}</Line>}
        </div>
      )}
    </div>
  );
}
